import { formatJsonValue, parseJsonValue } from '~/app/utils/json';

export type AssetLogDirection = 'inbound' | 'outbound';

export type AssetLogContentSummary = {
  source: string | null;
  action: string | null;
  frameType: string | null;
  direction: AssetLogDirection | null;
  uniqueId: string | null;
  connectorId: string | number | null;
  meterValueCount: number | null;
  summaryLabel: string;
  payload: unknown;
};

function ocppFrameType(messageTypeCode: number): string | null {
  if (messageTypeCode === 2) return 'CALL';
  if (messageTypeCode === 3) return 'CALLRESULT';
  if (messageTypeCode === 4) return 'CALLERROR';
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readDirection(value: unknown): AssetLogDirection | null {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'inbound' || normalized === 'incoming') {
    return 'inbound';
  }
  if (normalized === 'outbound' || normalized === 'outgoing') {
    return 'outbound';
  }
  return null;
}

function readConnectorId(value: unknown): string | number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return null;
}

function readMeterValueCount(record: Record<string, unknown>): number | null {
  const meterValue = record.meterValue ?? record.meterValues;
  if (Array.isArray(meterValue)) {
    return meterValue.length;
  }
  return null;
}

function buildSummaryLabel(action: string | null): string {
  return action?.trim() || '—';
}

function summarizeFromRecord(
  record: Record<string, unknown>,
  payload: unknown,
): AssetLogContentSummary {
  const action =
    readString(record.action) ??
    readString(record.messageType) ??
    readString(record.MessageType);
  const uniqueId =
    readString(record.uniqueId) ??
    readString(record.unique_id) ??
    readString(record.messageId);
  const frameType =
    readString(record.frameType) ?? readString(record.frame_type);
  const source = readString(record.source);
  const direction = readDirection(record.direction);
  const connectorId = readConnectorId(
    record.connectorId ?? record.connector_id,
  );
  const meterValueCount = readMeterValueCount(record);

  return {
    source,
    action,
    frameType,
    direction,
    uniqueId,
    connectorId,
    meterValueCount,
    summaryLabel: buildSummaryLabel(action),
    payload,
  };
}

function summarizeFromOcppArray(
  values: unknown[],
  payload: unknown,
): AssetLogContentSummary {
  const messageTypeCode =
    typeof values[0] === 'number' ? values[0] : Number(values[0]);
  const uniqueId = readString(values[1]);
  const frameType = Number.isFinite(messageTypeCode)
    ? ocppFrameType(messageTypeCode)
    : null;

  let action: string | null = null;
  let nestedPayload: Record<string, unknown> | null = null;

  if (frameType === 'CALL') {
    action = readString(values[2]);
    nestedPayload = asRecord(values[3]);
  } else {
    nestedPayload = asRecord(values[2]);
  }

  const connectorId = nestedPayload
    ? readConnectorId(nestedPayload.connectorId ?? nestedPayload.connector_id)
    : null;
  const meterValueCount = nestedPayload
    ? readMeterValueCount(nestedPayload)
    : null;

  return {
    source: null,
    action,
    frameType,
    // OCPP wire arrays do not carry direction — never invent it.
    direction: null,
    uniqueId,
    connectorId,
    meterValueCount,
    summaryLabel: buildSummaryLabel(action),
    payload,
  };
}

const summaryCache = new WeakMap<object, AssetLogContentSummary>();

function computeAssetLogContentSummary(
  rawLog: unknown,
): AssetLogContentSummary {
  const parsed = parseJsonValue(rawLog);

  if (parsed === null || parsed === undefined) {
    return {
      source: null,
      action: null,
      frameType: null,
      direction: null,
      uniqueId: null,
      connectorId: null,
      meterValueCount: null,
      summaryLabel: '—',
      payload: rawLog,
    };
  }

  if (Array.isArray(parsed)) {
    return summarizeFromOcppArray(parsed, parsed);
  }

  const record = asRecord(parsed);
  if (record) {
    return summarizeFromRecord(record, parsed);
  }

  return {
    source: null,
    action: null,
    frameType: null,
    direction: null,
    uniqueId: null,
    connectorId: null,
    meterValueCount: null,
    summaryLabel: '—',
    payload: parsed,
  };
}

/** Parse once per rawLog object; reused across table columns and client search. */
export function summarizeAssetLogContent(
  rawLog: unknown,
): AssetLogContentSummary {
  if (rawLog !== null && typeof rawLog === 'object') {
    const cached = summaryCache.get(rawLog);
    if (cached) {
      return cached;
    }
    const summary = computeAssetLogContentSummary(rawLog);
    summaryCache.set(rawLog, summary);
    return summary;
  }

  return computeAssetLogContentSummary(rawLog);
}

export async function copyAssetLogPayload(value: unknown): Promise<boolean> {
  if (value === null || value === undefined) {
    return false;
  }

  try {
    await navigator.clipboard.writeText(formatJsonValue(value));
    return true;
  } catch {
    return false;
  }
}
